import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { FunnelProvider } from "@/components/funnel";
import { HelmetProvider } from "react-helmet-async";
import { HashScrollHandler } from "@/components/HashScrollHandler";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import { initConsentedScripts } from "@/lib/loadConsentedScripts";

// Lazy load toast components to reduce initial bundle
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then(m => ({ default: m.Toaster }))
);
const Sonner = lazy(() =>
  import("@/components/ui/sonner").then(m => ({ default: m.Toaster }))
);

// Lazy load funnel modals (only loaded when funnel is triggered)
const UpsellModal = lazy(() =>
  import("@/components/funnel/UpsellModal").then(m => ({ default: m.UpsellModal }))
);
const DownsellModal = lazy(() =>
  import("@/components/funnel/DownsellModal").then(m => ({ default: m.DownsellModal }))
);

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OGImagePage = lazy(() => import("./pages/OGImage"));
const Auth = lazy(() => import("./pages/Auth"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const UpgradeSuccess = lazy(() => import("./pages/UpgradeSuccess"));
const UpgradeCancel = lazy(() => import("./pages/UpgradeCancel"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Admin = lazy(() => import("./pages/Admin"));
const Account = lazy(() => import("./pages/Account"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const DMCA = lazy(() => import("./pages/DMCA"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const PurchaseSuccess = lazy(() => import("./pages/PurchaseSuccess"));
const PurchaseCancel = lazy(() => import("./pages/PurchaseCancel"));
const FunnelSimulatorPage = lazy(() => import("./pages/FunnelSimulator"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Author = lazy(() => import("./pages/Author"));
const Glossary = lazy(() => import("./pages/Glossary"));
const Reports = lazy(() => import("./pages/Reports"));
const SubscriptionConfirmed = lazy(() => import("./pages/SubscriptionConfirmed"));
const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  useEffect(() => {
    initConsentedScripts();
  }, []);
  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FunnelProvider>
          <TooltipProvider>
            {/* Toast notifications - lazy loaded */}
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
            <BrowserRouter>
              <HashScrollHandler />
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/upgrade" element={<Upgrade />} />
                    <Route path="/upgrade/success" element={<UpgradeSuccess />} />
                    <Route path="/upgrade/cancel" element={<UpgradeCancel />} />
                    <Route path="/purchase/success" element={<PurchaseSuccess />} />
                    <Route path="/purchase/cancel" element={<PurchaseCancel />} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/dmca" element={<DMCA />} />
                    <Route path="/accessibility" element={<Accessibility />} />
                    <Route path="/og-image" element={<OGImagePage />} />
                    <Route path="/funnel-simulator" element={<FunnelSimulatorPage />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/blog/author/:slug" element={<Author />} />
                    <Route path="/glossary" element={<Glossary />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/subscription-confirmed" element={<SubscriptionConfirmed />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              {/* Global funnel modals - lazy loaded */}
              <Suspense fallback={null}>
                <UpsellModal />
                <DownsellModal />
              </Suspense>
              <CookieConsent />
            </BrowserRouter>
          </TooltipProvider>
        </FunnelProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
